import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ActionPreviewTableSection } from '../../../../../services/gable/gable.service';

/**
 * Generic `table` section — a column-headed grid of rows, used by any action
 * whose preview lists line items (journal entry debits and credits today).
 */
@Component({
    selector: 'app-ap-table-section',
    templateUrl: './table-section.component.html',
    styleUrls: ['./table-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActionPreviewTableSectionComponent {
    @Input({ required: true }) section!: ActionPreviewTableSection;

    /** Over this many rows the table opens collapsed to its first PREVIEW_ROWS. */
    static readonly COLLAPSE_OVER = 10;
    static readonly PREVIEW_ROWS = 5;

    showAll = false;

    get collapsible(): boolean {
        return (this.section.rows ?? []).length > ActionPreviewTableSectionComponent.COLLAPSE_OVER;
    }

    get columns(): string[] {
        return (this.section.columns ?? []).map((column) => this.text(column));
    }

    /** One row per line, full width: a Now/After pair reads "from → to", any other cell "Header value". */
    get listRows(): { heading: string; meta: string }[] {
        const columns = this.columns;
        const now = columns.indexOf('Now');
        const after = columns.indexOf('After');
        const isDiff = now > 0 && after > 0;
        const rows = this.collapsible && !this.showAll ? this.rows.slice(0, ActionPreviewTableSectionComponent.PREVIEW_ROWS) : this.rows;
        return rows.map((row) => {
            const rest = row.map((cell, i) => ({ header: columns[i] ?? '', cell })).slice(1).filter(({ cell }, i) => cell !== '' && (!isDiff || (i + 1 !== now && i + 1 !== after)));
            const parts = isDiff
                ? [...rest.map(({ cell }) => cell), `${row[now] || 'None'} → ${row[after] || 'None'}`]
                : rest.map(({ header, cell }) => `${header} ${cell}`.trim());
            return { heading: row[0] ?? '', meta: parts.join(' · ') };
        });
    }

    get rows(): string[][] {
        return (this.section.rows ?? []).map((row) => this.cells(row));
    }

    trackByIndex(index: number): number {
        return index;
    }

    private cells(row: unknown): string[] {
        if (Array.isArray(row)) {
            return row.map((cell) => this.text(cell));
        }

        if (row !== null && typeof row === 'object') {
            return this.section.columns.map((column) => this.text((row as Record<string, unknown>)[this.text(column)]));
        }

        return [this.text(row)];
    }

    private text(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return typeof value === 'string' ? value : String(value);
    }
}
